import { Injectable, inject, signal } from '@angular/core';
import { GraphQLClientService } from './graphql-client.service';
import { AnalyticsService } from './analytics.service';
import { CachePersistService } from './cache-persist.service';
import { MapPreferencesSyncService } from './map-preferences-sync.service';
import { ProfileSettingsSyncService } from './profile-settings-sync.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { isOk, makeError } from '../models/api-result';
import { createApiState } from '../models/api-state';
import { isTauri } from '../tauri-runtime';
import { DesktopExperienceStateService } from './desktop-experience-state.service';

/**
 * PlatformCustomerService — platform-mode customer switching.
 *
 * RESPONSIBILITY: list the customers the signed-in user can access
 * (`customers` query, driven server-side by their customerId + customerAccess[])
 * and switch the active workspace (`switchCustomer` mutation → a token scoped to
 * the TARGET customer's tenant). Internal/RDx users with >1 customer use this to
 * jump into a demo customer ("RICOH Workplace Demo") and exercise every app
 * end-to-end. Regular single-customer users never see a switcher.
 *
 * SIGNALS:
 *   customers — accessible customers (empty until load() resolves / on failure)
 *   loaded    — true once a load attempt has resolved (gates UI flicker)
 *   switchError — why the last switch failed (ApiError | null); the
 *                 switchers show it instead of silently resetting the row
 *
 * BACKEND CONTRACT: `customers` (query) + `switchCustomer` (mutation) on
 * rdx-platform-api. The switch is claims-based: createToken embeds
 * the target customerId and target Employee _id/roles plus a stable
 * homeEmployeeId. When no target Employee exists, roles is deliberately [] —
 * projecting home roles would be privilege escalation. switchCustomer ALSO
 * sets the auth
 * cookies (withCredentials) server-side; we additionally persist the returned
 * token to localStorage because Apollo sends `spaces_tenant`/`Authorization`
 * from there and the backend lets the header win over the cookie — a stale
 * home-tenant header would otherwise break the freshly-minted token.
 *
 * USED BY: ProfilePanelComponent ("Switch workspace" section) and the
 * workspace switcher dialog it opens for long lists.
 *
 * AI CONTEXT: best-effort load — a failure hides the switcher (switching is a
 * convenience, never load-bearing). switchTo hard-reloads so the whole app
 * re-resolves under the new customer. Because the Apollo cache is PERSISTED to
 * IndexedDB (CachePersistService), a hard reload alone would rehydrate the
 * previous customer's data — switchTo purges the persisted cache and clears
 * every customer-scoped localStorage key before reloading so nothing from the
 * old organisation bleeds into the new session.
 */

/** localStorage keys scoped to the ACTIVE customer — stale values from the
 *  previous organisation must not survive a switch. Auth keys (access-token,
 *  tenant, refresh-token, customer-id) are overwritten, not cleared. */
const CUSTOMER_SCOPED_STORAGE_KEYS = [
  'im-selected-location', // previous org's building id
  'im-boot-hint', // the last boot's building, floor and day for the early map read
  'schedule-selected-location-ids', // schedule multi-building selection
  'im-brand-color-cache', // previous org's brand colours + logo (flash on boot)
  'im-accent-division', // LEGACY: the Division accent's old cache (now inside im-brand-color-cache); cleared so it cannot linger
  // Offline-first caches of ProfileSettings, which is tenant-scoped
  // server-side: these hold the previous EMPLOYEE's values, not the person's.
  // `dropSyncBaseline()` clears the in-memory baseline but not the stored
  // payload, so without these the old fleet renders until hydration lands.
  'im-vehicles',
  'ct-dietary-preferences',
] as const;

/**
 * Prefixes to clear on switch, for stores that key per viewer or per draft.
 *
 * `im-meeting-drafts` is tenant + viewer scoped — `SessionService` says so and
 * clears every matching key on LOGOUT — but the switch never did, so the
 * previous customer's drafts survived into the next one. They reference that
 * customer's rooms and attendees, and an unsynced draft carries a
 * client-minted id, so leaving them in place risks pushing one into the
 * destination customer on the next write.
 */
const CUSTOMER_SCOPED_STORAGE_PREFIXES = [
  'im-meeting-drafts',
  // Start a hub's kept draft (HUB_DRAFT_PREFIX, features/hubs/create): it
  // names the previous organisation's people and subjects.
  'im-hub-create-draft',
] as const;

/** Longest a switch waits for the persisted cache to clear before reloading. */
export const SWITCH_PURGE_TIMEOUT_MS = 3000;

/** A customer the signed-in user can switch into. */
export interface PlatformCustomer {
  id: string;
  name: string;
  logo: string | null;
  /** Demo/test customers (isTest) are labelled distinctly in the UI. */
  isTest: boolean;
}

const CUSTOMERS_QUERY = `query customers {
  customers { _id name logo isTest }
}`;

const SWITCH_CUSTOMER_MUTATION = `mutation switchCustomer($customerId: String!) {
  switchCustomer(customerId: $customerId) {
    idToken
    refreshToken
    identityTenant
  }
}`;

interface CustomerNode {
  _id: string;
  name?: string | null;
  logo?: string | null;
  isTest?: boolean | null;
}

interface SwitchTokenNode {
  idToken?: string | null;
  refreshToken?: string | null;
  identityTenant?: string | null;
}

@Injectable({ providedIn: 'root' })
export class PlatformCustomerService {
  /** Platform boundary; overridable before construction without changing global runtime state. */
  protected desktopRuntime(): boolean {
    return isTauri;
  }

  private readonly graphql = inject(GraphQLClientService);
  private readonly desktopExperience = this.desktopRuntime()
    ? inject(DesktopExperienceStateService)
    : null;
  private readonly analytics = inject(AnalyticsService);
  private readonly cachePersist = inject(CachePersistService);
  private readonly mapPrefsSync = inject(MapPreferencesSyncService);
  private readonly profileSettingsSync = inject(ProfileSettingsSyncService);

  private readonly _customers = signal<PlatformCustomer[]>([]);
  readonly customers = this._customers.asReadonly();

  private readonly _loaded = signal<boolean>(false);
  readonly loaded = this._loaded.asReadonly();

  /** The last switch: why it failed, cleared when a new one starts. */
  private readonly _switch = createApiState<null>(null);
  readonly switchError = this._switch.error;

  /**
   * Forget the last switch's failure. A surface that shows it calls this
   * when it opens, so a failure from somewhere else (the org switcher, a
   * support-session return) is not shown later as if it were new.
   */
  clearSwitchError(): void {
    this._switch.clearError();
  }

  private loadInFlight: Promise<void> | null = null;

  /** Fetch the accessible-customers list once (single-flight, best-effort). */
  load(): Promise<void> {
    this.loadInFlight ??= this.doLoad().finally(() => {
      this.loadInFlight = null;
    });
    return this.loadInFlight;
  }

  private async doLoad(): Promise<void> {
    const res = await this.graphql.queryResult<CustomerNode[]>(
      CUSTOMERS_QUERY,
      undefined,
      'network-only',
    );
    if (isOk(res) && res.data) {
      this._customers.set(
        res.data
          .filter((c): c is CustomerNode => !!c?._id)
          .map((c) => ({
            id: c._id,
            name: c.name?.trim() || 'Unnamed customer',
            logo: c.logo?.trim() || null,
            isTest: c.isTest === true,
          })),
      );
    } else {
      this._customers.set([]); // older API / auth not ready — hide the switcher
    }
    this._loaded.set(true);
  }

  /** The customer id the session is currently scoped to (login/switch hint). */
  currentCustomerId(): string | null {
    try {
      return localStorage.getItem('customer-id');
    } catch {
      return null;
    }
  }

  /**
   * Switch into `customerId`: mint a tenant-scoped token, persist it (cookies
   * are set server-side; we mirror to localStorage so the Apollo headers stay
   * consistent), scrub every trace of the previous customer (persisted Apollo
   * cache, customer-scoped localStorage, synced map prefs), fire analytics,
   * then hard-reload. Returns false if the mutation yielded no token (caller
   * can surface a message). `source` attributes which surface triggered the
   * switch (profile panel vs the top-left organisation switcher).
   */
  async switchTo(
    customerId: string,
    source: 'profile' | 'header' | 'switcher-dialog' = 'profile',
  ): Promise<boolean> {
    const target = this._customers().find((c) => c.id === customerId);
    const from = this.currentCustomerId();

    this._switch.setLoading();
    const res = await this.graphql.mutateResult<SwitchTokenNode>(
      SWITCH_CUSTOMER_MUTATION,
      { customerId },
    );
    if (!isOk(res)) {
      this._switch.setError(res.error);
      return false;
    }
    if (!res.data?.idToken) {
      this._switch.setError(
        makeError('UNKNOWN', 'The switch did not return a session. Try again.'),
      );
      return false;
    }
    this._switch.setData(null);
    const token = res.data;

    // From here the new session is minted, so the only right ending is the
    // reload into it. Nothing below may strand the caller's spinner: a throw
    // is logged and the reload still happens.
    try {
      await this.prepareForReload(customerId, target?.name, from, source, token);
    } catch (err) {
      console.warn('[PlatformCustomerService] Pre-switch cleanup failed; reloading anyway:', err);
    }
    this.reloadApp();
    return true;
  }

  /** Everything between minting the new session and reloading into it. */
  private async prepareForReload(
    customerId: string,
    targetName: string | undefined,
    from: string | null,
    source: 'profile' | 'header' | 'switcher-dialog',
    token: SwitchTokenNode,
  ): Promise<void> {
    // The mutation has confirmed a replacement session. Fence old work before
    // tokens or persisted caches change, including while purge is awaiting I/O.
    this.desktopExperience?.invalidateScope('customer');

    try {
      localStorage.setItem('access-token', token.idToken as string);
      if (token.identityTenant) localStorage.setItem('tenant', token.identityTenant);
      if (token.refreshToken) localStorage.setItem('refresh-token', token.refreshToken);
      localStorage.setItem('customer-id', customerId);
    } catch {
      /* storage unavailable — the withCredentials cookies still carry the switch */
    }

    // Drop the previous customer's synced map theme so boot re-hydrates for
    // the new account instead of migrating the old account's local theme up.
    //
    // `dropSyncBaseline()`, NOT `reset()`. `reset()` is the logout path and
    // resets UserPreferences to DEFAULTS — `onboardingComplete: false` among
    // them — so switching customer re-ran the first-run tour every single time
    // and discarded the viewer's theme, lighting and accessibility choices.
    // The viewer is the same person on the same device; only the synced
    // baseline is customer-scoped.
    try {
      this.mapPrefsSync.dropSyncBaseline();
      this.profileSettingsSync.dropSyncBaseline();
    } catch {
      /* prefs reset is best-effort */
    }

    // Clear customer-scoped localStorage so the new session doesn't boot into
    // the previous org's building / schedule selection / brand colours.
    try {
      for (const key of CUSTOMER_SCOPED_STORAGE_KEYS) {
        localStorage.removeItem(key);
      }
      // Reverse iteration: removeItem re-indexes localStorage as we go.
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (
          key &&
          CUSTOMER_SCOPED_STORAGE_PREFIXES.some((p) => key.startsWith(p))
        ) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      /* storage unavailable — nothing to clear */
    }

    // CRITICAL: the Apollo cache is persisted to IndexedDB, so a hard reload
    // would otherwise rehydrate the OLD customer's data (bookings, people,
    // articles) into the new session. Purge before reloading.
    //
    // Bounded: purge is an IndexedDB call with no timeout of its own, and a
    // blocked database (another tab mid-upgrade) would otherwise leave the
    // switch spinning for ever.
    await Promise.race([
      this.cachePersist.purge(),
      new Promise<void>((resolve) => setTimeout(resolve, SWITCH_PURGE_TIMEOUT_MS)),
    ]);

    this.analytics.capture(ANALYTICS_EVENTS.CUSTOMER_SWITCHED, {
      target_customer_id: customerId,
      target_customer_name: targetName ?? 'unknown',
      from_customer_id: from ?? undefined,
      source,
    });
  }

  /** Hard reload into the app root — overridable in tests (jsdom has no nav). */
  protected reloadApp(): void {
    window.location.assign('/');
  }
}
